import { createContext, useCallback, useContext, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import type { ActionPhase, PhaseStatus } from '../hooks/useActionTracker';
import type { SavedWidget } from '../types/savedWidgets';
import type { ArtifactState, DraftArtifact } from '@/types/surfaceArtifacts';
import { useDraftArtifacts } from '../hooks/useDraftArtifacts';

/** Contact info for a department/agency */
export interface ContactInfo {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  url?: string | null;
  address?: string | null;
}

/** An AI-discovered field beyond the typed schema */
export interface AdditionalField {
  value: string;
  source?: string;
  confidence?: "high" | "medium" | "low";
}

/** A structured source reference (expanded format) */
export interface SourceRef {
  url: string;
  title?: string;
  accessDate?: string;
  fieldsProvided?: string[];
}

/**
 * Layer 2 Hydration result — populated by Gemini + Google Search grounding.
 * Supports both legacy (14-field) and expanded (100+ field) formats.
 * The `domains` key indicates the expanded format.
 */
export interface PropertyFacts {
  // Expanded format (v2) — present when hydrated with expanded schema
  domains?: {
    location?: Record<string, any>;
    parcel?: Record<string, any>;
    zoning?: Record<string, any>;
    regulations?: Record<string, any>;
    hazards?: Record<string, any>;
    utilities?: Record<string, any>;
    contacts?: Record<string, any>;
    financials?: Record<string, any>;
    infrastructure?: Record<string, any>;
  };
  fieldCount?: number;
  hydrationDate?: string;

  // Legacy format (v1) — present on older documents
  address?: string;
  location?: { county: string | null };
  parcel?: { apn: string | null; lotSize: number | null; livingArea: number | null; yearBuilt: number | null };
  zoning?: { zoningCode: string | null; aduAllowed: boolean | null; setbacks: string | null };
  hazards?: { floodZone: string | null; fireHazard: boolean | null; historicOverlay: boolean | null };

  // Shared fields (both formats)
  sources?: SourceRef[] | string[];
  confidence?: "high" | "medium" | "low";
  status?: "complete" | "error" | "in_progress" | "timeout" | "partial";
  lifecycleState?: "no_facts" | "queued" | "running" | "quality_retry_pending" | "quality_retrying" | "report_ready" | "report_generating" | "report_complete" | "terminal_degraded" | "failed";
  hydratedAt?: any;
  hydratedBy?: string;
  error?: string | null;
  domainsCompleted?: number;
  domainsTotal?: number;
  reportReady?: boolean;
  generationEligible?: boolean;
  dataReady?: boolean;
  internalRetryStatus?: string;
  hydrationQuality?: {
    status?: "ready" | "partial_ok" | "internal_retry" | "degraded";
    reasons?: string[];
    lowYieldDomains?: string[];
  };
  coreDomainReady?: boolean;
  coreDomainReadiness?: {
    status?: "ready" | "degraded";
    required?: string[];
    completed?: string[];
    missing?: string[];
    reason?: "missing_core_domains";
  };
}

export interface CanvasDirectiveState {
  primaryArtifactId?: string;
  artifactState?: ArtifactState;
  artifactTitle?: string;
  isDraft?: boolean;
  hasUnsavedChanges?: boolean;
}

function getUiSpecTitle(uiSpec: { props?: Record<string, unknown>; component?: string } | undefined): string | undefined {
  const title = uiSpec?.props?.title;
  if (typeof title === 'string' && title.trim()) return title.trim();
  const heading = uiSpec?.props?.heading;
  if (typeof heading === 'string' && heading.trim()) return heading.trim();
  return uiSpec?.component;
}

function getArtifactDirectiveTitle(artifact: DraftArtifact | SavedWidget | undefined): string | undefined {
  if (!artifact) return undefined;
  return artifact.title || artifact.label || getUiSpecTitle(artifact.ui_spec);
}

// All data from Dash.jsx hooks, distributed to the 4-pane layout tree via context.
// Hooks run once in Dash.jsx — this context distributes, never duplicates subscriptions.
export interface DashDataContextValue {
  // useProjectData
  project: any;
  projectLoading: boolean;
  tasks: any[];
  activities: any[];
  actionTracker: any;
  // useProjectReport
  reportData: any;
  reportLoading: boolean;
  latestReportData: any;
  patchHistory: any[];
  // useScoreComparison / useScoreHistory
  scoreDelta: number | null;
  trend: string;
  latestGrade: string | null;
  scoreHistory: any[];
  // useFourTArtifacts / useAllProjectArtifacts
  fourTArtifacts: any;
  allArtifacts: any[];
  // useSavedWidgets — chat-inline GenUI promoted to durable workspace via Save button
  savedWidgets: SavedWidget[];
  savedWidgetsLoading: boolean;
  // useDraftArtifacts — Wave 1 #2 Workspace Canvas auto-mount stream
  // Drafts are TTL-bound (Firestore native expireAt). Primary writer is the
  // backend `mount_canvas_artifact` tool; the frontend listener is the read
  // path consumed by WorkspaceCanvasTab.
  draftArtifacts: DraftArtifact[];
  draftArtifactsLoading: boolean;
  // Active artifact pointer — array form to support Wave 1 #11 multi-canvas
  // Compare. The first id is the "primary" (focused) canvas.
  activeArtifactIds: string[];
  setActiveArtifactIds: Dispatch<SetStateAction<string[]>>;
  artifactCanvasStates: Record<string, ArtifactState>;
  setArtifactCanvasState: (artifactId: string, state: ArtifactState | null) => void;
  // Derived: first activeArtifactIds entry, falling back to the newest draft.
  // Null when there are no drafts and no explicit pointer.
  primaryArtifactId: string | null;
  // Directive Engine Wave 1 #10 — local L5 canvas focus signal. Present only
  // when the workspace canvas is the active capability and an artifact is mounted.
  canvasState: CanvasDirectiveState | null;
  // useBudgetHydration / useBudgetScenario
  costBuilderProps: any;
  budgetHydrationLoading: boolean;
  budgetHydrationError: string | null;
  budgetHydrationErrorMessage: string | null;
  saveBudgetScenario: (...args: any[]) => any;
  // useAuth
  currentUser: any;
  userEntitlements: string[];
  userAccount: any;
  // Task CRUD (from useProjectData hook)
  addTask: (...args: any[]) => any;
  updateTask: (...args: any[]) => any;
  deleteTask: (...args: any[]) => any;
  // Capability persistence (Wave 3)
  saveDesignSelection: (...args: any[]) => any;
  saveFinancingSelection: (...args: any[]) => any;
  saveROIScenario: (...args: any[]) => any;
  saveContactInteraction: (...args: any[]) => any;
  savePermitLinkVisit: (...args: any[]) => any;
  // Layer 2 factual data (assessment from Firestore)
  assessmentData: any;
  // Layer 2 Hydration (Gemini + Google Search grounding)
  propertyFacts: PropertyFacts | null;
  // User notes (from useProjectData hook)
  notes: any[];
  // Aggregated citation sources (from useProjectData hook)
  projectSources: any[];
  addNote: (...args: any[]) => any;
  updateNote: (...args: any[]) => any;
  deleteNote: (...args: any[]) => any;
  // Wave 4: Complex persistence hooks
  permitProgress: any;
  savePermitProgress: (...args: any[]) => any;
  timelineProgress: any;
  updatePhaseStatus: (...args: any[]) => any;
  // Action phases (centralized — one Firestore listener, consumed by 4+ capabilities)
  actionPhases: ActionPhase[];
  actionPhasesLoading: boolean;
  actionPhasesError: Error | null;
  retryActionPhases: () => void;
  updateActionPhaseStatus: (phaseId: string, newStatus: PhaseStatus) => Promise<void>;
  projectProgress: any;
  // Systems-First: the single phase the user should act on next — derived once
  // in Dash.jsx from Project State progress when present, with legacy
  // action_phases fallback for older projects.
  currentPhase: ActionPhase | null;
  // Capability state (lives in Dash.jsx, surfaced via context for layout access)
  activeCapability: string | null;
  setActiveCapability: (id: string | null) => void;
  // Questionnaire modal trigger
  openQuestionnaire: () => void;
}

const DashDataContext = createContext<DashDataContextValue>({
  project: null,
  projectLoading: true,
  tasks: [],
  activities: [],
  actionTracker: null,
  reportData: null,
  reportLoading: true,
  latestReportData: null,
  patchHistory: [],
  scoreDelta: null,
  trend: 'stable',
  latestGrade: null,
  scoreHistory: [],
  fourTArtifacts: null,
  allArtifacts: [],
  savedWidgets: [],
  savedWidgetsLoading: true,
  draftArtifacts: [],
  draftArtifactsLoading: true,
  activeArtifactIds: [],
  setActiveArtifactIds: () => {},
  artifactCanvasStates: {},
  setArtifactCanvasState: () => {},
  primaryArtifactId: null,
  canvasState: null,
  costBuilderProps: null,
  budgetHydrationLoading: false,
  budgetHydrationError: null,
  budgetHydrationErrorMessage: null,
  saveBudgetScenario: () => {},
  addTask: () => {},
  updateTask: () => {},
  deleteTask: () => {},
  saveDesignSelection: () => {},
  saveFinancingSelection: () => {},
  saveROIScenario: () => {},
  saveContactInteraction: () => {},
  savePermitLinkVisit: () => {},
  currentUser: null,
  userEntitlements: [],
  userAccount: {},
  assessmentData: null,
  propertyFacts: null,
  notes: [],
  projectSources: [],
  addNote: () => {},
  updateNote: () => {},
  deleteNote: () => {},
  permitProgress: null,
  savePermitProgress: () => {},
  timelineProgress: null,
  updatePhaseStatus: () => {},
  actionPhases: [],
  actionPhasesLoading: true,
  actionPhasesError: null,
  retryActionPhases: () => {},
  updateActionPhaseStatus: async () => {},
  projectProgress: null,
  currentPhase: null,
  activeCapability: null,
  setActiveCapability: () => {},
  openQuestionnaire: () => {},
});

/**
 * Caller-supplied subset of the context value — everything Dash.jsx already
 * builds and passes in. The provider augments this with Wave 1 #2 canvas
 * fields (`draftArtifacts`, `activeArtifactIds`, `primaryArtifactId`, etc.)
 * by running `useDraftArtifacts` exactly once internally.
 *
 * Why hoist `useDraftArtifacts` into the provider rather than Dash.jsx?
 *   1. Phase 4b centralization invariant is preserved (one call, one listener).
 *   2. Dash.jsx is owned by a different checkpoint — keeps C2 scoped to this
 *      file as required by the C2 spec ("DO NOT modify any other files").
 *   3. The provider becomes the canonical home for surface-artifact state —
 *      future Wave 1 #3 pill-machine state can land here next to it.
 */
type DashDataProviderInput = Omit<
  DashDataContextValue,
  | 'draftArtifacts'
  | 'draftArtifactsLoading'
  | 'activeArtifactIds'
  | 'setActiveArtifactIds'
  | 'artifactCanvasStates'
  | 'setArtifactCanvasState'
  | 'primaryArtifactId'
  | 'canvasState'
>;

export function DashDataProvider({
  value,
  children,
}: {
  value: DashDataProviderInput;
  children: ReactNode;
}) {
  const projectId: string | null | undefined = value.project?.id ?? null;
  const { drafts: draftArtifacts, loading: draftArtifactsLoading } =
    useDraftArtifacts(projectId);

  const [activeArtifactIds, setActiveArtifactIds] = useState<string[]>([]);
  const [artifactCanvasStates, setArtifactCanvasStates] =
    useState<Record<string, ArtifactState>>({});

  const setArtifactCanvasState = useCallback(
    (artifactId: string, state: ArtifactState | null) => {
      if (!artifactId) return;
      setArtifactCanvasStates((prev) => {
        if (state === null) {
          const next = { ...prev };
          delete next[artifactId];
          return next;
        }
        return { ...prev, [artifactId]: state };
      });
    },
    [],
  );

  const primaryArtifactId = useMemo<string | null>(() => {
    if (activeArtifactIds.length > 0) return activeArtifactIds[0];
    return draftArtifacts[0]?.id ?? null;
  }, [activeArtifactIds, draftArtifacts]);

  const canvasState = useMemo<CanvasDirectiveState | null>(() => {
    if (value.activeCapability !== 'workspace_artifact' || !primaryArtifactId) {
      return null;
    }

    const draft = draftArtifacts.find((artifact) => artifact.id === primaryArtifactId);
    const saved = value.savedWidgets.find((widget) => widget.id === primaryArtifactId);
    const artifact = draft ?? saved;
    if (!artifact) return null;

    const artifactState: ArtifactState | undefined = draft?.state ?? (
      saved ? artifactCanvasStates[saved.id] ?? 'saved' : undefined
    );

    return {
      primaryArtifactId,
      artifactState,
      artifactTitle: getArtifactDirectiveTitle(artifact),
      isDraft: !!draft,
      hasUnsavedChanges:
        artifactState === 'draft' ||
        artifactState === 'draft_saved' ||
        artifactState === 'editing' ||
        artifactState === 'modified',
    };
  }, [
    artifactCanvasStates,
    draftArtifacts,
    primaryArtifactId,
    value.activeCapability,
    value.savedWidgets,
  ]);

  const merged: DashDataContextValue = useMemo(
    () => ({
      ...value,
      draftArtifacts,
      draftArtifactsLoading,
      activeArtifactIds,
      setActiveArtifactIds,
      artifactCanvasStates,
      setArtifactCanvasState,
      primaryArtifactId,
      canvasState,
    }),
    [
      value,
      draftArtifacts,
      draftArtifactsLoading,
      activeArtifactIds,
      artifactCanvasStates,
      setArtifactCanvasState,
      primaryArtifactId,
      canvasState,
    ]
  );

  return (
    <DashDataContext.Provider value={merged}>
      {children}
    </DashDataContext.Provider>
  );
}

export function useDashData(): DashDataContextValue {
  const ctx = useContext(DashDataContext);
  if (!ctx) {
    throw new Error('useDashData must be used within a DashDataProvider');
  }
  return ctx;
}

export default DashDataContext;
